import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  Divider,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Science as ScienceIcon,
  LocalHospital as TreatmentIcon,
  Medication as MedicationIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Biotech as BiotechIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { 
  getCancerDiagnoses, 
  getTreatmentPlans 
} from '../../services/oncologyService';
import { getChemotherapyCycles } from '../../services/chemotherapyService';

// Sub-tab panel component
const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const statusColors = {
  'Active': 'error',
  'In Remission': 'success',
  'Recurrence': 'warning',
  'Resolved': 'info',
  'Deceased': 'default',
  'Draft': 'default',
  'Completed': 'success',
  'On Hold': 'warning',
  'Cancelled': 'error',
  'Scheduled': 'info',
  'In Progress': 'primary'
};

const PatientOncologyTab = ({ patient }) => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [diagnoses, setDiagnoses] = useState([]);
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [chemoCycles, setChemoCycles] = useState([]);

  const patientId = patient?.id;
  const facilityId = defaultFacility?.id;

  // Load diagnoses
  const loadDiagnoses = useCallback(async () => {
    if (!patientId || !facilityId) return;
    try {
      const result = await getCancerDiagnoses(patientId, facilityId);
      setDiagnoses(result.data || []);
    } catch (err) {
      console.error('Error loading diagnoses:', err);
    }
  }, [patientId, facilityId]);

  // Load treatment plans
  const loadTreatmentPlans = useCallback(async () => {
    if (!patientId || !facilityId) return;
    try {
      const result = await getTreatmentPlans(patientId, facilityId);
      setTreatmentPlans(result.data || []);
    } catch (err) {
      console.error('Error loading treatment plans:', err);
    }
  }, [patientId, facilityId]);

  // Load chemo cycles
  const loadChemoCycles = useCallback(async () => {
    if (!patientId || !facilityId) return;
    try {
      const result = await getChemotherapyCycles(patientId, facilityId, { limit: 50 });
      setChemoCycles(result.data || []);
    } catch (err) {
      console.error('Error loading chemo cycles:', err);
    }
  }, [patientId, facilityId]);

  // Load data based on active tab
  useEffect(() => {
    if (!patientId || !facilityId) return;
    
    setLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        if (subTab === 0) await loadDiagnoses();
        else if (subTab === 1) await loadTreatmentPlans();
        else if (subTab === 2) await loadChemoCycles();
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [subTab, patientId, facilityId, loadDiagnoses, loadTreatmentPlans, loadChemoCycles]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Diagnoses Tab Content
  const DiagnosesContent = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {diagnoses.length} {diagnoses.length === 1 ? 'diagnosis' : 'diagnoses'} recorded
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`/oncology/diagnosis/new/${patientId}`)}>
          New Diagnosis
        </Button>
      </Box>
      
      {diagnoses.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No cancer diagnoses recorded for this patient.
        </Typography>
      ) : (
        <List dense disablePadding>
          {diagnoses.map((dx, idx) => (
            <React.Fragment key={dx.id}>
              {idx > 0 && <Divider />}
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{dx.cancerType}</Typography>
                      <Chip label={dx.status} size="small" color={statusColors[dx.status] || 'default'} sx={{ height: 20 }} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" component="span">
                        {dx.primarySite} • Stage: {dx.staging?.clinicalStage || dx.staging?.pathologicalStage || 'N/A'}
                        {dx.staging?.tStage && ` (${dx.staging.tStage}${dx.staging.nStage}${dx.staging.mStage})`}
                        • Dx: {formatDate(dx.diagnosisDate)}
                      </Typography>
                    </Box>
                  }
                />
                <Box>
                  <Tooltip title="View details">
                    <IconButton size="small" onClick={() => navigate(`/oncology/diagnosis/edit/${dx.id}`)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => navigate(`/oncology/diagnosis/edit/${dx.id}`)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );

  // Treatment Plans Tab Content
  const TreatmentPlansContent = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {treatmentPlans.length} treatment plan{treatmentPlans.length !== 1 ? 's' : ''}
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`/oncology/treatment-plan/new/${patientId}`)}>
          New Plan
        </Button>
      </Box>
      
      {treatmentPlans.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No treatment plans created for this patient.
        </Typography>
      ) : (
        <List dense disablePadding>
          {treatmentPlans.map((plan, idx) => (
            <React.Fragment key={plan.id}>
              {idx > 0 && <Divider />}
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{plan.planName}</Typography>
                      <Chip label={plan.status} size="small" color={statusColors[plan.status] || 'default'} sx={{ height: 20 }} />
                      <Chip label={plan.intent} size="small" variant="outlined" sx={{ height: 20 }} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" component="span">
                        {plan.modalities?.join(', ') || 'No modalities'}
                        {plan.plannedStartDate && ` • Start: ${formatDate(plan.plannedStartDate)}`}
                        {plan.chemotherapy?.regimenName && ` • ${plan.chemotherapy.regimenName}`}
                      </Typography>
                    </Box>
                  }
                />
                <Box>
                  <Tooltip title="View details">
                    <IconButton size="small" onClick={() => navigate(`/oncology/treatment-plan/view/${plan.id}`)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => navigate(`/oncology/treatment-plan/edit/${plan.id}`)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );

  // Chemotherapy Tab Content
  const ChemotherapyContent = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {chemoCycles.length} chemo cycle{chemoCycles.length !== 1 ? 's' : ''} scheduled
        </Typography>
        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
          onClick={() => navigate(`/oncology/chemotherapy/tracker/${patientId}`)}>
          Full Tracker
        </Button>
      </Box>
      
      {chemoCycles.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No chemotherapy cycles scheduled for this patient.
        </Typography>
      ) : (
        <List dense disablePadding>
          {chemoCycles.slice(0, 5).map((cycle, idx) => (
            <React.Fragment key={cycle.id}>
              {idx > 0 && <Divider />}
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Cycle {cycle.cycleNumber} of {cycle.totalPlannedCycles}
                      </Typography>
                      <Chip label={cycle.status} size="small" color={statusColors[cycle.status] || 'default'} sx={{ height: 20 }} />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {cycle.regimenName || 'Unknown regimen'} • {formatDate(cycle.plannedStartDate)}
                    </Typography>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
          {chemoCycles.length > 5 && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', py: 1, cursor: 'pointer' }}
              onClick={() => navigate(`/oncology/chemotherapy/tracker/${patientId}`)}>
              + {chemoCycles.length - 5} more cycles
            </Typography>
          )}
        </List>
      )}
    </Box>
  );

  // Quick Actions Tab Content
  const QuickActionsContent = () => (
    <Grid container spacing={1.5}>
      <Grid item xs={6} sm={4}>
        <Button fullWidth variant="outlined" size="small" startIcon={<AssessmentIcon />}
          onClick={() => navigate(`/oncology/assessments/performance/${patientId}`)}
          sx={{ justifyContent: 'flex-start', py: 1, textTransform: 'none' }}>
          Performance Status
        </Button>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Button fullWidth variant="outlined" size="small" startIcon={<WarningIcon />}
          onClick={() => navigate(`/oncology/assessments/toxicity/${patientId}`)}
          sx={{ justifyContent: 'flex-start', py: 1, textTransform: 'none' }}>
          Toxicity
        </Button>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Button fullWidth variant="outlined" size="small" startIcon={<PersonIcon />}
          onClick={() => navigate(`/oncology/survivorship/plan/${patientId}`)}
          sx={{ justifyContent: 'flex-start', py: 1, textTransform: 'none' }}>
          Survivorship
        </Button>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Button fullWidth variant="outlined" size="small" startIcon={<BiotechIcon />}
          onClick={() => navigate(`/oncology/clinical-trials/enrollment/${patientId}`)}
          sx={{ justifyContent: 'flex-start', py: 1, textTransform: 'none' }}>
          Clinical Trials
        </Button>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Button fullWidth variant="outlined" size="small" startIcon={<TimelineIcon />}
          onClick={() => navigate(`/oncology/treatment-timeline/${patientId}`)}
          sx={{ justifyContent: 'flex-start', py: 1, textTransform: 'none' }}>
          Timeline
        </Button>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Button fullWidth variant="outlined" size="small" startIcon={<MedicationIcon />}
          onClick={() => navigate(`/oncology/chemotherapy/dose-calculator/${patientId}`)}
          sx={{ justifyContent: 'flex-start', py: 1, textTransform: 'none' }}>
          Dose Calculator
        </Button>
      </Grid>
    </Grid>
  );

  if (!patientId) {
    return (
      <Alert severity="warning">Patient ID is required to view oncology information.</Alert>
    );
  }

  return (
    <Box>
      {/* Sub-tabs */}
      <Tabs
        value={subTab}
        onChange={(_, newValue) => setSubTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem', textTransform: 'none' }
        }}
      >
        <Tab icon={<ScienceIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Diagnoses (${diagnoses.length})`} />
        <Tab icon={<TreatmentIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Plans (${treatmentPlans.length})`} />
        <Tab icon={<MedicationIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Chemo (${chemoCycles.length})`} />
        <Tab icon={<AssessmentIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="More Actions" />
      </Tabs>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Tab content */}
      {!loading && (
        <>
          <TabPanel value={subTab} index={0}><DiagnosesContent /></TabPanel>
          <TabPanel value={subTab} index={1}><TreatmentPlansContent /></TabPanel>
          <TabPanel value={subTab} index={2}><ChemotherapyContent /></TabPanel>
          <TabPanel value={subTab} index={3}><QuickActionsContent /></TabPanel>
        </>
      )}
    </Box>
  );
};

export default PatientOncologyTab;
